import React, { useState, useEffect, useCallback } from 'react';
import { Col, Row, Spin } from "antd";
import BubblePlot from "@containers/facility/Benchmark/Chart/BubblePlot";
import BarChart from "@containers/facility/Benchmark/Chart/BarChart";
import { api } from "@redux/api";
import _ from 'lodash';
import { colors } from "@containers/facility/Benchmark/Chart/colors";
import { jsforceResponse } from "@config/httpService";
import { useSelector } from 'react-redux';
import { FacilityBenchmarkFilters } from "@/containers/facility/Benchmark/FacilityBenchmarkInner/FacilityBenchmarkFilters";
import { removeDuplicates } from "@components/help";
import moment from "moment";
import ResponsiveModal from "@components/ResponsiveModal";


const AllFacilityBenchmark = ({
    isH5 = false,
    filterDrawerOpen = false,
    onCloseFilterDrawer = () => {},
}) => {

    const [chart1_data, setChart1Data] = useState([]);
    const [chart2_data, setChart2Data] = useState([]);
    const [chart3_data, setChart3Data] = useState([]);
    const [colors_type, setColorsType] = useState([]);
    const [loading, setLoading] = useState(false);

    // 原始数据和筛选相关状态
    const [rawData, setRawData] = useState([]);
    const [filtered_start_year, setFilteredStartYear] = useState(null);
    const [filtered_end_year, setFilteredEndYear] = useState(null);
    const [filtered_region, setFilteredRegion] = useState([]);
    const [filtered_city, setFilteredCity] = useState([]);
    const [filtered_state, setFilteredState] = useState([]);
    const [filtered_country, setFilteredCountry] = useState([]);
    const [filtered_category, setFilteredCategory] = useState([]);
    const [filtered_climate_zone, setFilteredClimateZone] = useState([]);

    // 筛选器选项
    const [region_options, setRegionOptions] = useState([]);
    const [city_options, setCityOptions] = useState([]);
    const [state_options, setStateOptions] = useState([]);
    const [country_options, setCountryOptions] = useState([]);
    const [category_options, setCategoryOptions] = useState([]);
    const [climate_zone_options, setClimateZoneOptions] = useState([]);
    const [start_min_year, setStartMinYear] = useState(null);
    const [end_max_year, setEndMaxYear] = useState(null);
    const [facilityCategoryOptions, setFacilityCategoryOptions] = useState([]);

    const CurrentCompany = useSelector(state => state.Facility?.selected?.record?.company_id);
    const CurrentFacility = useSelector(state => state.Facility?.selected?.Id);

    // 根据 facility_category ID 获取对应的名称
    const getFacilityCategoryName = (categoryId) => {
        if (!categoryId) return '-';
        const category = facilityCategoryOptions.find(item => item.id === categoryId);
        return category?.name || categoryId;
    };

    // 前端筛选方法
    const applyClientSideFilters = useCallback((data) => {
        let filteredData = data;

        // 年份范围筛选
        if (filtered_start_year) {
            filteredData = filteredData.filter(item => 
                item.year && parseInt(item.year) >= parseInt(filtered_start_year)
            );
        }
        if (filtered_end_year) {
            filteredData = filteredData.filter(item => 
                item.year && parseInt(item.year) <= parseInt(filtered_end_year)
            );
        }

        // 地区筛选
        if (filtered_region && filtered_region.length > 0) {
            filteredData = filteredData.filter(item => 
                filtered_region.includes(item.region)
            );
        }

        // 城市筛选
        if (filtered_city && filtered_city.length > 0) {
            filteredData = filteredData.filter(item => 
                filtered_city.includes(item.city)
            );
        }

        // 州/省筛选
        if (filtered_state && filtered_state.length > 0) {
            filteredData = filteredData.filter(item => 
                filtered_state.includes(item.state)
            );
        }

        // 国家筛选
        if (filtered_country && filtered_country.length > 0) {
            filteredData = filteredData.filter(item => 
                filtered_country.includes(item.country)
            );
        }

        // 类别筛选（使用 facility_category 字段）
        if (filtered_category && filtered_category.length > 0) {
            filteredData = filteredData.filter(item => 
                filtered_category.includes(item.facility_category)
            );
        }

        // 气候区筛选
        if (filtered_climate_zone && filtered_climate_zone.length > 0) {
            filteredData = filteredData.filter(item => 
                filtered_climate_zone.includes(item.climate_zone)
            );
        }

        return filteredData;
    }, [filtered_start_year, filtered_end_year, filtered_region, filtered_city, filtered_state, filtered_country, filtered_category, filtered_climate_zone]);

    // 生成筛选器选项（基于原始数据）
    const generateFilterOptions = useCallback((data) => {
        let climate_zone_opts = [], region_data = [], city_data = [], state_data = [], country_data = [], min_year = null, max_year = null;

        for (let i = 0; i < data.length; i++) {
            if (data[i].climate_zone) {
                climate_zone_opts.push({label: data[i].climate_zone, value: data[i].climate_zone});
            }
            // category_options 现在从接口加载，不再从数据中提取
            if (data[i].region) {
                region_data.push({label: data[i].region, value: data[i].region});
            }
            if (data[i].city) {
                city_data.push({label: data[i].city, value: data[i].city});
            }
            if (data[i].state) {
                state_data.push({label: data[i].state, value: data[i].state});
            }
            if (data[i].country) {
                country_data.push({label: data[i].country, value: data[i].country});
            }
            if (min_year) {
                min_year = Math.min(min_year, data[i].year)
            } else {
                min_year = data[i].year
            }
            if (max_year) {
                max_year = Math.max(max_year, data[i].year)
            } else {
                max_year = data[i].year
            }
        }

        setClimateZoneOptions(removeDuplicates(climate_zone_opts));
        // category_options 不在这里更新，保留从接口加载的数据
        setRegionOptions(removeDuplicates(region_data));
        setCityOptions(removeDuplicates(city_data));
        setStateOptions(removeDuplicates(state_data));
        setCountryOptions(removeDuplicates(country_data));
        setStartMinYear(min_year);
        setEndMaxYear(max_year);
    }, []);

    // 加载设施类别枚举
    useEffect(() => {
        const fetchFacilityCategories = async () => {
            try {
                const response = await api.getFacilityCategory();
                if (response.status === 200) {
                    // 现在接口不再分页，直接返回数组
                    const categories = Array.isArray(response.data) ? response.data : (response.data?.results || []);
                    setFacilityCategoryOptions(categories);
                    
                    // 转换为 Select 组件需要的格式
                    // value 使用 ID，label 使用名称
                    const categoryOptions = categories.map(item => ({
                        label: item.name || item.category_name,
                        value: item.id
                    }));
                    setCategoryOptions(categoryOptions);
                }
            } catch (error) {
                console.error('Failed to load facility categories:', error);
            }
        };
        fetchFacilityCategories();
    }, []);

    // 当 CurrentCompany 改变时，清空 rawData 缓存
    useEffect(() => {
        setRawData([]);
    }, [CurrentCompany]);

    useEffect(() => {
        const fetchData = async () => {
            if (!CurrentCompany) return;
            
            setLoading(true);
            
            let result;
            
            // 首次加载时，获取所有数据并缓存
            if (rawData.length === 0) {
                let allDataResult = await api.getAllFacilityBenchmark('company=' + CurrentCompany);
                allDataResult = jsforceResponse(allDataResult);
                allDataResult = _.get(allDataResult, 'data.results') || [];
                
                setRawData(allDataResult);
                generateFilterOptions(allDataResult);
                result = allDataResult;
            } else {
                // 后续筛选直接使用缓存的原始数据，避免重复请求后端
                result = rawData;
            }
            
            // 应用所有前端筛选
            let all_facilities = applyClientSideFilters(result);
            let chart1_data = [], chart2_data = [], chart3_data = [], colors_type = [];
            let isCurrentId = 0;

            for (let i = 0; i < all_facilities.length; i++) {

                if (all_facilities[i]['facility_id'] === CurrentFacility) {
                    isCurrentId = all_facilities[i]['id'];
                    all_facilities[i]['isCurrent'] = true
                }

                colors_type[all_facilities[i]['category']] = colors[i]

                const year = all_facilities[i]['year'] ? + all_facilities[i]['year'] + '' : '';
                const eui = all_facilities[i]['eui'] ? + all_facilities[i]['eui'] : '';
                const energy_costs_sqft = all_facilities[i]['energy_costs_sqft'] ? + all_facilities[i]['energy_costs_sqft'] : '';
                const size = all_facilities[i]['size'] ? + all_facilities[i]['size'] : '';
                const category = all_facilities[i]['category'] ? all_facilities[i]['category'] : '';
                const isCurrent = all_facilities[i]['facility_id'] === CurrentFacility;
                const facilityName = all_facilities[i]['facility'] ? all_facilities[i]['facility'] : '';

                if (year && eui && size) {
                    chart1_data.push({
                        facilityName,
                        key: i,
                        xAxis: year,
                        yAxis: eui,
                        size,
                        type: category,
                        isCurrent,
                    })
                }

                if (year && energy_costs_sqft && size) {
                    chart2_data.push({
                        facilityName,
                        key: i,
                        xAxis: year,
                        yAxis: energy_costs_sqft,
                        size,
                        type: category,
                        isCurrent,
                    })
                }
                // chart2_data.push({
                //     key: i,
                //     xAxis: all_facilities[i]['year'],
                //     yAxis: all_facilities[i]['energy_costs_sqft'],
                //     size: parseInt(all_facilities[i]['size']),
                //     type: all_facilities[i]['category'],
                //     isCurrent: all_facilities[i]['company_id'] === CurrentCompany
                // })

                chart3_data.push({
                    facilityName,
                    key: i.toString(),
                    xField: all_facilities[i]['facility'],
                    yField: all_facilities[i]['eui'] ? all_facilities[i]['eui'] : 0,
                    type: all_facilities[i]['category'],
                    isCurrent: all_facilities[i]['facility_id'] === CurrentFacility
                })

            }

            chart1_data = chart1_data.sort((a, b) => a.xAxis - b.xAxis)
            chart2_data = chart2_data.sort((a, b) => a.xAxis - b.xAxis)
            chart3_data = chart3_data.sort((a, b) => b.yField - a.yField)

            // isCurrentIndex = chart3_data.findIndex(e => e.id === isCurrentId)

            // chart3_data = chart3_data
            setChart1Data(chart1_data);
            setChart2Data(chart2_data);
            setChart3Data(chart3_data);
            setColorsType(colors_type);
            setLoading(false);

        }
        fetchData();
    }, [CurrentCompany, CurrentFacility, rawData, filtered_start_year, filtered_end_year, filtered_region, filtered_city, filtered_state, filtered_country, filtered_category, filtered_climate_zone, applyClientSideFilters, generateFilterOptions]);

    // 处理筛选器变化
    const handleFilter = (filters) => {
        setFilteredStartYear(filters.year_range && filters.year_range[0] ? moment(filters.year_range[0]).year() : null);
        setFilteredEndYear(filters.year_range && filters.year_range[1] ? moment(filters.year_range[1]).year() : null);
        setFilteredRegion(filters.region || []);
        setFilteredCity(filters.city || []);
        setFilteredState(filters.state || []);
        setFilteredCountry(filters.country || []);
        setFilteredCategory(filters.category || []);
        setFilteredClimateZone(filters.climate_zone || []);
    };

    const renderFilter = (isMobileDrawer = false) => (
        <FacilityBenchmarkFilters
            start_min_year={start_min_year}
            end_max_year={end_max_year}
            climate_zone_options={climate_zone_options}
            category_options={category_options}
            region_options={region_options}
            city_options={city_options}
            state_options={state_options}
            country_options={country_options}
            handleFilter={handleFilter}
            isMobileDrawer={isMobileDrawer}
        />
    );

    return (
        <Spin spinning={loading}>
            <div className="p-4">
                {/* 筛选器 */}
                {!isH5 ? (
                    <div className="mb-4 p-4 bg-white rounded-lg shadow-sm">
                        {renderFilter(false)}
                    </div>
                ) : null}

                <Row className={'w-full'} gutter={[16, 16]}>
                    <Col xs={24} md={12} xl={12}>

                        <BubblePlot fillOpacity={0.5}
                            opacity={0.65}
                            data={chart1_data} title='EUI by Year Built'
                            xLabel='Year Built' yLabel='kBtu/sq-yr'
                            colors={colors_type}
                            showTip={false}
                            />

                    </Col>
            <Col xs={24} md={12} xl={12}>

                <BubblePlot fillOpacity={0.5}
                    opacity={0.65}
                    xLabel='Year Built' yLabel='$/SQFT'
                    data={chart2_data} title='Energy Costs by Year Built'
                    colors={colors_type}
                    showTip={false}
                    />


            </Col>
            <Col xs={24} md={24} xl={24}>

                <BarChart data={chart3_data}
                    title='EUI (kBtu/sq-yr)'
                    fillOpacity={1}
                    opacity={1}
                    xLabel='Facility' yLabel='EUI'
                    showTip={false}
                    />
            </Col>

                </Row>
            </div>
            {isH5 ? (
                <ResponsiveModal
                    title="Filter"
                    open={filterDrawerOpen}
                    onCancel={onCloseFilterDrawer}
                    footer={null}
                    height="82vh"
                    bodyStyle={{ paddingBottom: 16 }}
                >
                    {renderFilter(true)}
                </ResponsiveModal>
            ) : null}
        </Spin>
    );
}

export default AllFacilityBenchmark;
